import { useState, useEffect, useMemo, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Select,
  Result,
  PageContainer,
  PageHeader,
  InfoCard,
  Badge,
  BackButton,
  ActionButtonsRow,
  CopyHTMLButton,
  MathDiv,
} from '../../../styles/global'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
class BuildingType {
  constructor(group, type, wasteContribution, freshSludgeContribution, unit) {
    this.group = group
    this.type = type
    this.wasteContribution = wasteContribution
    this.freshSludgeContribution = freshSludgeContribution
    this.unit = unit
  }
}

export function PlumbingSepticTank() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const buildingTypes = useMemo(
    () => [
      new BuildingType('Ocupantes permanentes', 'Residência alto padrão', 160, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Residência médio padrão', 130, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Residência baixo padrão', 100, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Hotel', 100, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Hotel com cozinha e lavanderia', 240, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Hotel com cozinha, lavanderia e banheira', 240, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Alojamento provisório', 80, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Orfanato e asilo', 120, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Escola (internato)', 150, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Presídio', 240, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Quartel', 240, 1, 'pessoas'),
      new BuildingType('Ocupantes permanentes', 'Área rural', 240, 1, 'pessoas'),
      new BuildingType('Ocupantes temporários', 'Fábrica em geral', 70, 0.3, 'pessoas'),
      new BuildingType('Ocupantes temporários', 'Escritório', 50, 0.2, 'pessoas'),
      new BuildingType('Ocupantes temporários', 'Edifício público ou comercial', 50, 0.2, 'pessoas'),
      new BuildingType('Ocupantes temporários', 'Escola de meio período', 50, 0.2, 'pessoas'),
      new BuildingType('Ocupantes temporários', 'Escola de período integral', 100, 0.3, 'pessoas'),
      new BuildingType('Ocupantes temporários', 'Creche', 50, 0.2, 'pessoas'),
      new BuildingType('Ocupantes temporários', 'Bar', 6, 0.1, 'pessoas'),
      new BuildingType('Ocupantes temporários', 'Restaurante e similar', 25, 0.1, 'refeições'),
      new BuildingType(
        'Ocupantes temporários',
        'Cinema, teatro, templo, igreja e local de curta permanência',
        2,
        0.02,
        'lugares'
      ),
      new BuildingType('Ocupantes temporários', 'Ambulatório', 25, 0.2, 'pessoas'),
      new BuildingType(
        'Ocupantes temporários',
        'Estação ferroviária, rodoviária e metroviária',
        25,
        0.2,
        'passageiros'
      ),
      new BuildingType('Ocupantes temporários', 'Sanitário público', 480, 4, 'bacias sanitárias'),
    ],
    []
  )

  const [numberOfPeopleOrUnits, setNumberOfPeopleOrUnits] = useState('')
  const [temperature, setTemperature] = useState('')
  const [cleaningInterval, setCleaningInterval] = useState(1)
  const [selectedGroup, setSelectedGroup] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [selectedShape, setSelectedShape] = useState('')
  const [filteredTypes, setFilteredTypes] = useState([])
  const [filteredShapeModes, setFilteredShapeModes] = useState([])
  const [selectedShapeMode, setSelectedShapeMode] = useState('')
  const [width, setWidth] = useState('')
  const [length, setLength] = useState('')
  const [height, setHeight] = useState('')
  const [diameter, setDiameter] = useState('')
  const [data, setData] = useState('')
  const [result, setResult] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const shapesOptions = useMemo(() => {
    return [
      { format: 'Retangular', mode: 'Obter largura' },
      { format: 'Retangular', mode: 'Obter comprimento' },
      { format: 'Retangular', mode: 'Obter profundidade' },
      { format: 'Retangular', mode: 'Comprimento e largura 2:1' },
      { format: 'Cilíndrico', mode: 'Obter diâmetro' },
      { format: 'Cilíndrico', mode: 'Obter profundidade' },
    ]
  }, [])

  const groups = useMemo(() => {
    return [...new Set(buildingTypes.map((bt) => bt.group))]
  }, [buildingTypes])
  const shapes = useMemo(() => {
    return [...new Set(shapesOptions.map((s) => s.format))]
  }, [shapesOptions])

  const handleGroupChange = (event) => {
    const group = event.target.value
    setSelectedGroup(group)

    if (group) {
      const types = buildingTypes.filter((bt) => bt.group === group)
      setFilteredTypes(types)
    } else {
      setFilteredTypes([])
    }
  }

  const handleShapeChange = (event) => {
    const shape = event.target.value
    setSelectedShape(shape)

    if (shape) {
      const modes = shapesOptions.filter((s) => s.format === shape)
      setFilteredShapeModes(modes)
      setSelectedShapeMode(modes[0].mode)
    } else {
      setFilteredShapeModes([])
    }
  }

  const handleTypeChange = (event) => {
    const type = event.target.value
    setSelectedType(type)
  }

  const HISTORY_KEY = 'plumbing-septic-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]

      setSelectedGroup(entry.selectedGroup)
      setSelectedType(entry.selectedType)
      setFilteredTypes(entry.filteredTypes)
      setNumberOfPeopleOrUnits(entry.numberOfPeopleOrUnits)
      setTemperature(entry.temperature)
      setCleaningInterval(entry.cleaningInterval)
      setSelectedShape(entry.selectedShape)
      setFilteredShapeModes(entry.filteredShapeModes)
      setSelectedShapeMode(entry.selectedShapeMode)
      setWidth(entry.width)
      setLength(entry.length)
      setHeight(entry.height)
      setDiameter(entry.diameter)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback(
    (name, value) => {
      if (selectedShape == 'Retangular') {
        if (name == 'diameter') return null
        if (name == 'width' && ['Obter largura', 'Comprimento e largura 2:1'].includes(selectedShapeMode)) return null
        if (name == 'length' && ['Obter comprimento', 'Comprimento e largura 2:1'].includes(selectedShapeMode))
          return null
        if (name == 'height' && ['Obter profundidade'].includes(selectedShapeMode)) return null
      } else {
        if (name == 'width') return null
        if (name == 'length') return null
        if (name == 'diameter' && ['Obter diâmetro'].includes(selectedShapeMode)) return null
        if (name == 'height' && ['Obter profundidade'].includes(selectedShapeMode)) return null
      }

      if (value === '' || value === null || value === undefined) {
        return 'Campo obrigatório'
      }

      const numValue = Number(value)

      if (isNaN(numValue)) {
        return 'Valor deve ser um número'
      }

      if (numValue <= 0) {
        return 'Valor deve ser maior que 0'
      }

      return null
    },
    [selectedShape, selectedShapeMode]
  )

  useEffect(() => {
    const newErrors = {}

    newErrors.numberOfPeopleOrUnits = validateField('numberOfPeopleOrUnits', numberOfPeopleOrUnits)
    newErrors.temperature = validateField('temperature', temperature)
    newErrors.width = validateField('width', width)
    newErrors.length = validateField('length', length)
    newErrors.height = validateField('height', height)
    newErrors.diameter = validateField('diameter', diameter)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [numberOfPeopleOrUnits, temperature, width, length, height, diameter, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  useEffect(() => {
    if (groups.length > 0) {
      setSelectedGroup(groups[0])
      const types = buildingTypes.filter((bt) => bt.group === groups[0])
      setFilteredTypes(types)
      setSelectedType(types[0]?.type || '')
    }
  }, [groups, buildingTypes])

  useEffect(() => {
    if (shapes.length > 0) {
      setSelectedShape(shapes[0])
      const modes = shapesOptions.filter((s) => s.format === shapes[0])
      setFilteredShapeModes(modes)
      setSelectedShapeMode(modes[0]?.mode || '')
    }
  }, [shapes, shapesOptions])

  function handleSave() {
    const stateData = {
      numberOfPeopleOrUnits,
      temperature,
      cleaningInterval,
      selectedGroup,
      selectedType,
      selectedShape,
      filteredTypes,
      filteredShapeModes,
      selectedShapeMode,
      width,
      length,
      height,
      diameter,
      data,
      result,
    }

    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Flexão.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleLoad(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setNumberOfPeopleOrUnits(jsonData.numberOfPeopleOrUnits)
        setTemperature(jsonData.temperature)
        setCleaningInterval(jsonData.cleaningInterval)
        setSelectedGroup(jsonData.selectedGroup)
        setSelectedType(jsonData.selectedType)
        setSelectedShape(jsonData.selectedShape)
        setFilteredTypes(jsonData.filteredTypes)
        setFilteredShapeModes(jsonData.filteredShapeModes)
        setSelectedShapeMode(jsonData.selectedShapeMode)
        setWidth(jsonData.width)
        setLength(jsonData.length)
        setHeight(jsonData.height)
        setDiameter(jsonData.diameter)
        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const building = buildingTypes.find((bt) => bt.type === selectedType)

      const shapeOption = shapesOptions.findIndex(
        (option) => option.format === selectedShape && option.mode === selectedShapeMode
      )

      const data = {
        numberOfPeopleOrUnits,
        wasteContribution: building.wasteContribution,
        freshSludgeContribution: building.freshSludgeContribution,
        temperature,
        cleaningInterval,
        width,
        length,
        diameter,
        height,
        shapeOption,
      }

      setData(data)

      const response = await api.post('/plumbing/septic-tank', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        selectedGroup,
        selectedType,
        filteredTypes,
        numberOfPeopleOrUnits,
        temperature,
        cleaningInterval,
        selectedShape,
        filteredShapeModes,
        selectedShapeMode,
        width,
        length,
        height,
        diameter,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const headerGradient = 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)'

  return (
    <PageContainer>
      <PageHeader $gradient={headerGradient}>
        <div className="header-content">
          <BackButton to="/hidrossanitario">
            <FiArrowLeft /> Voltar para Hidrossanitário
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/hidrossanitario">Hidrossanitário</a>
            <span>/</span>
            <span>Fossa séptica</span>
          </div>
          <h1>Dimensionamento de fossa séptica</h1>
          <p className="subtitle">
            Calcule volume e dimensões mínimas com base no tipo de ocupação, contribuições de esgoto e intervalo de
            limpeza.
          </p>
          <div className="badges">
            <Badge>NBR 7229</Badge>
            <Badge>NBR 13969</Badge>
            <Badge>Volume e geometria</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)"
        $border="2px solid #06b6d4"
        $primaryColor="#0e7490"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Selecione o tipo de edificação para obter as contribuições de esgoto e lodo fresco, escolha o formato do
              tanque e informe o intervalo de limpeza.
            </p>
            <p>
              Ajuste largura, comprimento, profundidade ou diâmetro conforme a geometria adotada e confira alertas sobre
              dimensões mínimas recomendadas.
            </p>
            <div className="norms">
              <span>NBR 7229</span>
              <span>NBR 13969</span>
              <span>Instalações prediais</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <h3>Dados de uso</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>Tipo de edificação</span>
              <div>
                <Select style={{ marginLeft: '0' }} id="groupSelect" value={selectedGroup} onChange={handleGroupChange}>
                  {groups.map((group, index) => (
                    <option key={index} value={group}>
                      {group}
                    </option>
                  ))}
                </Select>
                <Select
                  style={{ marginLeft: '0' }}
                  id="typeSelect"
                  disabled={!selectedGroup}
                  onChange={handleTypeChange}
                  value={selectedType}
                >
                  {filteredTypes.map((type, index) => (
                    <option key={index} value={type.type}>
                      {type.type}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Número de {selectedType && buildingTypes.find((bt) => bt.type === selectedType).unit}</span>
              <ValidatedInput
                name="numberOfPeopleOrUnits"
                type="number"
                value={numberOfPeopleOrUnits || ''}
                onChange={(event) => setNumberOfPeopleOrUnits(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Temperatura ambiente (ºC)</span>
              <ValidatedInput
                name="temperature"
                type="number"
                value={temperature || ''}
                onChange={(event) => setTemperature(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Intervalo de limpeza (anos)</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="cleaningIntervalSelect"
                  value={cleaningInterval}
                  onChange={(event) => setCleaningInterval(Number(event.target.value))}
                >
                  <option key={1} value={1}>
                    1
                  </option>
                  <option key={2} value={2}>
                    2
                  </option>
                  <option key={3} value={3}>
                    3
                  </option>
                  <option key={4} value={4}>
                    4
                  </option>
                  <option key={5} value={5}>
                    5
                  </option>
                </Select>
              </div>
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Formato</span>
              <div>
                <Select style={{ marginLeft: '0' }} id="shapeSelect" value={selectedShape} onChange={handleShapeChange}>
                  {shapes.map((shape, index) => (
                    <option key={index} value={shape}>
                      {shape}
                    </option>
                  ))}
                </Select>
                <Select
                  style={{ marginLeft: '0' }}
                  id="shapeModeSelect"
                  disabled={!selectedShape}
                  value={selectedShapeMode}
                  onChange={(event) => setSelectedShapeMode(event.target.value)}
                >
                  {filteredShapeModes.map((shapeOption, index) => (
                    <option key={index} value={shapeOption.mode}>
                      {shapeOption.mode}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            {(selectedShapeMode == 'Obter comprimento' ||
              (selectedShapeMode == 'Obter profundidade' && selectedShape == 'Retangular')) && (
              <InputDiv>
                <span>Largura (m)</span>
                <ValidatedInput
                  name="width"
                  type="number"
                  value={width || ''}
                  onChange={(event) => setWidth(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {(selectedShapeMode == 'Obter largura' ||
              (selectedShapeMode == 'Obter profundidade' && selectedShape == 'Retangular')) && (
              <InputDiv>
                <span>Comprimento (m)</span>
                <ValidatedInput
                  name="length"
                  type="number"
                  value={length || ''}
                  onChange={(event) => setLength(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {(selectedShapeMode == 'Obter largura' ||
              selectedShapeMode == 'Obter comprimento' ||
              selectedShapeMode == 'Comprimento e largura 2:1' ||
              selectedShapeMode == 'Obter diâmetro') && (
              <InputDiv>
                <span>Profundidade (m)</span>
                <ValidatedInput
                  name="height"
                  type="number"
                  value={height || ''}
                  onChange={(event) => setHeight(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {selectedShapeMode == 'Obter profundidade' && selectedShape == 'Cilíndrico' && (
              <InputDiv>
                <span>Diâmetro (m)</span>
                <ValidatedInput
                  name="diameter"
                  type="number"
                  value={diameter || ''}
                  onChange={(event) => setDiameter(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleLoad} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Dimensões</h3>
              <MathDiv>
                <BlockMath math={`V = ${result.volume} \\text{ L}`} />
                {data.shapeOption == 0 && (
                  <>
                    <BlockMath math={`b = ${result.width} \\text{ m}`} />
                  </>
                )}
                {data.shapeOption == 1 && (
                  <>
                    <BlockMath math={`l = ${result.length} \\text{ m}`} />
                  </>
                )}
                {data.shapeOption == 2 && (
                  <>
                    <BlockMath math={`h = ${result.height} \\text{ m}`} />
                  </>
                )}
                {data.shapeOption == 3 && (
                  <>
                    <BlockMath math={`b= ${result.width} \\text{ m}`} />
                    <BlockMath math={`l = ${result.length} \\text{ m}`} />
                  </>
                )}
                {data.shapeOption == 4 && (
                  <>
                    <BlockMath math={`d = ${result.diameter} \\text{ m}`} />
                  </>
                )}
                {data.shapeOption == 5 && (
                  <>
                    <BlockMath math={`h  = ${result.height} \\text{ m}`} />
                  </>
                )}
              </MathDiv>
              {result.warnings.length > 0 && (
                <>
                  <h3>Considerações</h3>
                  <MathDiv>
                    {result.warnings.map((w) => (
                      <p key={w}>{w}</p>
                    ))}
                  </MathDiv>
                </>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Tempo de detenção</h3>
              <MathDiv>
                <BlockMath
                  math={`Volume = N \\cdot C = ${data.numberOfPeopleOrUnits} \\cdot ${data.wasteContribution} = ${result.retentionPeriod.totalContribution} \\text{ L/dia}`}
                />
                <p>Para essa contribuição o tempo de detenção deve ser de {result.retentionPeriod.value} dias.</p>
              </MathDiv>
              <h3>Taxa de acumulação total de lodo</h3>
              <MathDiv>
                <p>Para esse intervalo de limpeza e essa temperatura o fator K vale {result.sludgeAccumulationRate}.</p>
              </MathDiv>
              <h3>Volume</h3>
              <MathDiv>
                <BlockMath math={`V = 1000 + N \\cdot \\left( C \\cdot T + K \\cdot L_f \\right)`} />
                <BlockMath
                  math={`V = 1000 + ${data.numberOfPeopleOrUnits} \\cdot \\left( ${data.wasteContribution} \\cdot ${result.retentionPeriod.value} + ${result.sludgeAccumulationRate} \\cdot ${data.freshSludgeContribution} \\right)`}
                />
                <BlockMath math={`\\boxed{V = ${result.volume} \\text{ L}}`} />
              </MathDiv>
              <h3>Dimensões</h3>
              <MathDiv>
                {data.shapeOption == 0 && (
                  <>
                    <BlockMath
                      math={`b = \\dfrac{V}{l \\cdot h} = \\dfrac{${result.volume / 1000}}{${data.length} \\cdot ${data.height}} = ${result.width} \\text{ m}`}
                    />
                  </>
                )}
                {data.shapeOption == 1 && (
                  <>
                    <BlockMath
                      math={`l = \\dfrac{V}{b \\cdot h} = \\dfrac{${result.volume / 1000}}{${data.width} \\cdot ${data.height}} = ${result.length} \\text{ m}`}
                    />
                  </>
                )}
                {data.shapeOption == 2 && (
                  <>
                    <BlockMath
                      math={`h = \\dfrac{V}{b \\cdot l} = \\dfrac{${result.volume / 1000}}{${data.width} \\cdot ${data.length}} = ${result.height} \\text{ m}`}
                    />
                  </>
                )}
                {data.shapeOption == 3 && (
                  <>
                    <BlockMath
                      math={`b = \\left( \\dfrac{V}{2 \\cdot h} \\right)^{\\dfrac{1}{2}} = \\left( \\dfrac{${result.volume / 1000}}{2 \\cdot ${data.height}} \\right)^{\\dfrac{1}{2}} = ${result.width} \\text{ m}`}
                    />
                    <BlockMath math={`l = 2 \\cdot b= 2 \\cdot ${result.width} = ${result.length} \\text{ m}`} />
                  </>
                )}
                {data.shapeOption == 4 && (
                  <>
                    <BlockMath
                      math={`d = \\left( \\dfrac{4 \\cdot V}{\\pi \\cdot h} \\right)^{\\dfrac{1}{2}} = \\left( \\dfrac{4 \\cdot ${result.volume / 1000}}{\\pi \\cdot ${data.height}} \\right)^{\\dfrac{1}{2}} = ${result.diameter} \\text{ m}`}
                    />
                  </>
                )}
                {data.shapeOption == 5 && (
                  <>
                    <BlockMath
                      math={`h = \\left( \\dfrac{4 \\cdot V}{\\pi \\cdot d^2} \\right)^{\\dfrac{1}{2}} = \\left( \\dfrac{4 \\cdot ${result.volume / 1000}}{\\pi \\cdot ${data.diameter}^2} \\right)^{\\dfrac{1}{2}} = ${result.height} \\text{ m}`}
                    />
                  </>
                )}
              </MathDiv>
              {result.warnings.length > 0 && (
                <>
                  <h3>Considerações</h3>
                  <MathDiv>
                    {result.warnings.map((w) => (
                      <p key={w}>{w}</p>
                    ))}
                  </MathDiv>
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
